import { AxiosInstance } from 'axios';
import {
  SearchRequest,
  SearchResponse
} from '../types/search';
import { buildOffsetParams } from '../util/pagination';
import {
  SearchRequestSchema,
  SearchResponseSchema
} from '../z/search';

export class SearchClient {
  constructor(
    private readonly http: AxiosInstance
    
  ) {}

  /**
   * Search across markets, events, and profiles.
   * 
   * This endpoint provides full-text search capabilities across all content
   * types in the Polymarket ecosystem. Results are ranked by relevance score
   * and can be filtered by type, category, and tags.
   * 
   * @example
   * ```typescript
   * // Basic search
   * const results = await client.search.query({ q: 'bitcoin' });
   * 
   * // Search specific types
   * const marketResults = await client.search.query({ 
   *   q: 'election', 
   *   types: ['market', 'event'] 
   * });
   * 
   * // Search with filters
   * const filteredResults = await client.search.query({
   *   q: 'politics',
   *   category: 'politics',
   *   tags: ['elections'],
   *   limit: 20
   * });
   * ```
   * 
   * @param params - Search parameters
   * @param params.q - Search query (required)
   * @param params.limit - Number of results to return (default: 10)
   * @param params.offset - Number of results to skip (default: 0)
   * @param params.types - Filter by result types: 'market', 'event', 'profile'
   * @param params.category - Filter by category
   * @param params.tags - Filter by tags (array of tag IDs or slugs)
   * 
   * @returns Promise<SearchResponse> - Search results with relevance scores
   * @throws {Error} When the search query is invalid or the API request fails
   * 
   * @see {@link https://docs.polymarket.com/api-reference/search/search-markets-events-and-profiles} - API Documentation
   */
  async query(params: SearchRequest): Promise<SearchResponse> {
    const validatedParams = SearchRequestSchema.parse(params);
    const queryParams = buildOffsetParams(validatedParams);
            try {
      const response = await this.http.get('/search', { params: queryParams });
      const validatedData = SearchResponseSchema.parse(response.data);
      
            return validatedData as SearchResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to search for "${validatedParams.q}": ${error.message}`);
      }
      throw error;
    }
  }
}
